import { FC, useState } from "react";
import { Box, Button, Typography } from "@mui/material";

import { RiLinkM } from "react-icons/ri";
import WarningAmberIcon from "../../../../icons/WarningAmberIcon";

import { Claim, EdiObject } from "../../../../../types/claims";
import JoinPaymentDialog from "./JoinPaymentDialog";
import { DuplicatePaymentPair, summarizePayment } from "./joinPayments";

const PaymentLine: FC<{ label: string; payment?: EdiObject }> = ({
  label,
  payment,
}) => (
  <Typography variant="body2" sx={{ color: "#92400E", fontSize: "0.8125rem" }}>
    <Box component="span" sx={{ fontWeight: 600 }}>
      {label}:
    </Box>{" "}
    {payment ? summarizePayment(payment) : "no longer on the claim"}
  </Typography>
);

/**
 * A possible_duplicate_payments warning: an 835 arrived with the same payer
 * tier and amount as a manual payment, without a check/EFT or payer claim # to
 * join them automatically. Admins can join them from here.
 */
const DuplicatePaymentWarningCard: FC<{
  pair: DuplicatePaymentPair;
  claim: Claim;
  ediObjects: EdiObject[];
  canJoin: boolean;
  onJoined: () => void;
}> = ({ pair, claim, ediObjects, canJoin, onJoined }) => {
  const [isJoinOpen, setIsJoinOpen] = useState(false);
  const { manual, remit, warning } = pair;
  // Another manual payment may already be joined to this 835: the API refuses a second
  const isRemitTaken = !!remit?.matchedManual;

  return (
    <Box
      data-testid="duplicate-payment-warning"
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        p: 1.5,
        mb: 1,
        backgroundColor: "#FFFBEB",
        border: "1px solid #F59E0B",
        borderRadius: 1,
      }}
    >
      <WarningAmberIcon color="#D97706" size={20} />
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <Typography variant="body2" sx={{ color: "#92400E", fontWeight: 600 }}>
          Possible duplicate payment
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "#92400E", fontSize: "0.8125rem" }}
        >
          An 835 arrived with the same payer level and amount as a manual
          payment. If they're the same payment, join them so it counts once.
        </Typography>
        <PaymentLine label="Manual payment" payment={manual} />
        <PaymentLine label="835 remit" payment={remit} />

        {canJoin && manual && remit && (
          <Box
            sx={{
              pt: 0.5,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 0.5,
            }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<RiLinkM size={14} />}
              disabled={isRemitTaken}
              onClick={() => setIsJoinOpen(true)}
              sx={{ bgcolor: "common.white" }}
            >
              Join
            </Button>
            {isRemitTaken && (
              <Typography sx={{ fontSize: "0.75rem", color: "#92400E" }}>
                Another manual payment is already joined to this 835. If this
                one is a duplicate, remove it instead.
              </Typography>
            )}
            <JoinPaymentDialog
              open={isJoinOpen}
              claim={claim}
              manual={manual}
              ediObjects={ediObjects}
              remitAttachmentId={warning.remitAttachmentId}
              onClose={() => setIsJoinOpen(false)}
              onJoined={onJoined}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DuplicatePaymentWarningCard;
